import { type FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useSnapCarousel } from 'react-snap-carousel';
import { Box, IconButton, Typography } from '@mui/material';
import { trackEvent } from '@surfline/web-common';

import { ChevronLeft } from 'components/Icons/ChevronLeft';
import { ChevronRight } from 'components/Icons/ChevronRight';
import WavetrakLink from 'components/WavetrakLink';
import {
  useMinWidthTablet,
  useMinWidthDesktop,
  useMinWidthDesktopLarge,
} from 'hooks/useMediaQueries';
import type { SpotRecommendation } from 'common/api/spotRecommendations';
import type { Units } from 'types/units';
import type { UserFavorites } from 'types/userFavorites';

import FavoriteSlide from './FavoriteSlide';
import RecommendationSlide from './RecommendationSlide';
import SlidePlaceholder from './SlidePlaceholder';

import styles from './FavoritesCarouselSnap.module.scss';

const LAZY_MOUNT_PAGE_RADIUS = 1; // render slides within +/- 1 page of the active page
const FETCH_NEXT_PAGE_BUFFER = 2; // request more recs when within 2 pages of the loaded end

interface FavoritesCarouselSnapProps {
  callToActionButton?: {
    link: string;
    text: string;
    trackingEvent?: {
      event: string;
      properties: Record<string, string>;
    };
  };
  favorites: UserFavorites;
  isActive?: boolean;
  isLoadingRecommendations?: boolean;
  mode?: string;
  nextTracking?: {
    event: string;
    properties: Record<string, string>;
  };
  prevTracking?: {
    event: string;
    properties: Record<string, string>;
  };
  showSectionTitles?: boolean;
  title?: string;
  units: Units;
  recommendations?: Array<SpotRecommendation>;
  onReachingEnd?: () => void;
  onRecommendationFavorited?: (spotId: string) => void;
  onRecommendationImpression?: (recommendation: SpotRecommendation, index: number) => void;
  onRecommendationVisibleOnActivePage?: (spotId: string) => void;
  onRecommendationHiddenFromActivePage?: (spotId: string) => void;
  scrollResetKey?: string;
}

type SlideEntry =
  | { kind: 'favorite'; favorite: UserFavorites[number]; key: string }
  | { kind: 'recommendation'; recommendation: SpotRecommendation; key: string };

// Pixels over which the "Favorites" title fades as user scrolls toward recommendations
const FADE_DISTANCE = 200;
const FAVORITE_GRACE_MS = 1500;

const FavoritesCarouselSnap: FunctionComponent<FavoritesCarouselSnapProps> = ({
  callToActionButton,
  favorites,
  isActive: isCarouselActive = true,
  isLoadingRecommendations = false,
  mode,
  nextTracking,
  prevTracking,
  showSectionTitles = false,
  title,
  units,
  recommendations = [],
  onReachingEnd,
  onRecommendationFavorited,
  onRecommendationImpression,
  onRecommendationVisibleOnActivePage,
  onRecommendationHiddenFromActivePage,
  scrollResetKey,
}) => {
  // Delay favorites updates briefly when a recommendation is favorited so
  // the pill success state is visible before the card moves.
  const [delayedFavorites, setDelayedFavorites] = useState(favorites);
  const prevFavIdsRef = useRef(new Set(favorites.map((f) => f._id)));
  const graceTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    const recIds = new Set(recommendations.map((r) => r._id));
    const hasNewFromRecs = favorites.some(
      (f) => !prevFavIdsRef.current.has(f._id) && recIds.has(f._id),
    );
    prevFavIdsRef.current = new Set(favorites.map((f) => f._id));
    if (hasNewFromRecs) {
      clearTimeout(graceTimer.current);
      graceTimer.current = setTimeout(() => setDelayedFavorites(favorites), FAVORITE_GRACE_MS);
    } else {
      setDelayedFavorites(favorites);
    }
    return () => clearTimeout(graceTimer.current);
  }, [favorites, recommendations]);

  const slideEntries = useMemo<Array<SlideEntry>>(() => {
    const favoriteIds = new Set(delayedFavorites.map((f) => f._id));
    const favoriteEntries: Array<SlideEntry> = delayedFavorites.map((favorite) => ({
      kind: 'favorite',
      favorite,
      key: `favorite-${favorite._id}`,
    }));
    const recommendationEntries: Array<SlideEntry> = recommendations
      .filter((rec) => !favoriteIds.has(rec._id))
      .map((recommendation) => ({
        kind: 'recommendation',
        recommendation,
        key: `recommendation-${recommendation._id}`,
      }));
    return [...favoriteEntries, ...recommendationEntries];
  }, [delayedFavorites, recommendations]);

  const {
    activePageIndex,
    hasNextPage,
    hasPrevPage,
    next,
    pages,
    prev,
    refresh,
    scrollRef,
    snapPointIndexes,
  } = useSnapCarousel(slideEntries.length ? { initialPages: [[0]] } : undefined);

  const scrollElRef = useRef<HTMLElement | null>(null);
  const combinedScrollRef = useCallback(
    (el: HTMLElement | null) => {
      scrollRef(el as HTMLElement | null);
      scrollElRef.current = el;
    },
    [scrollRef],
  );

  const firstRecIndex = useMemo(
    () => slideEntries.findIndex((e) => e.kind === 'recommendation'),
    [slideEntries],
  );
  const hasFavorites = favorites.length > 0;
  const hasRecs = firstRecIndex >= 0;

  const titleBarRef = useRef<HTMLDivElement | null>(null);
  const favTitleRef = useRef<HTMLSpanElement | null>(null);
  const recTitleRef = useRef<HTMLSpanElement | null>(null);

  // Scroll-linked section title animation: slides "Recommended spots" in from the right
  // and fades "Favorites" out as the user scrolls past the favorites/recommendations boundary
  useEffect(() => {
    const scrollEl = scrollElRef.current;
    const titleBarEl = titleBarRef.current;
    if (!scrollEl || !titleBarEl || !showSectionTitles || !hasRecs || !hasFavorites)
      return undefined;

    const update = () => {
      const recItem = scrollEl.children[firstRecIndex] as HTMLElement | undefined;
      if (!recItem) return;
      const offset = recItem.getBoundingClientRect().left - titleBarEl.getBoundingClientRect().left;
      const clamped = Math.max(0, Math.min(offset, titleBarEl.clientWidth));
      if (recTitleRef.current) {
        recTitleRef.current.style.transform = `translateX(${clamped}px)`;
      }
      if (favTitleRef.current) {
        favTitleRef.current.style.opacity = String(Math.min(1, clamped / FADE_DISTANCE));
      }
    };

    update();
    scrollEl.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(scrollEl);
    return () => {
      scrollEl.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [showSectionTitles, hasRecs, hasFavorites, firstRecIndex]);

  const hasScrollResetKeyMounted = useRef(false);
  useEffect(() => {
    if (scrollResetKey === undefined) return;
    if (!hasScrollResetKeyMounted.current) {
      hasScrollResetKeyMounted.current = true;
      return;
    }
    if (scrollElRef.current) {
      scrollElRef.current.scrollLeft = 0;
    }
  }, [scrollResetKey]);

  const isMinWidthTablet = useMinWidthTablet();
  const isMinWidthDesktop = useMinWidthDesktop();
  const isMinWidthDesktopLarge = useMinWidthDesktopLarge();

  const onClickCallToActionButton = useCallback(() => {
    if (callToActionButton?.trackingEvent) {
      trackEvent(
        callToActionButton.trackingEvent.event,
        callToActionButton.trackingEvent.properties,
      );
    }
  }, [callToActionButton]);

  const onClickPrevious = useCallback(() => {
    if (prevTracking) {
      trackEvent(prevTracking.event, prevTracking.properties);
    }
    prev();
  }, [prev, prevTracking]);

  const onClickNext = useCallback(() => {
    if (nextTracking) {
      trackEvent(nextTracking.event, nextTracking.properties);
    }
    next();
  }, [next, nextTracking]);

  const slidesPerPage = useMemo(() => {
    if (isMinWidthDesktopLarge) return 4;
    if (isMinWidthDesktop) return 3;
    if (isMinWidthTablet) return 2;
    return 1;
  }, [isMinWidthDesktop, isMinWidthDesktopLarge, isMinWidthTablet]);

  const lazyMountEnabled = recommendations.length > 0;

  const slides = useMemo(() => {
    const totalPages = pages.length;
    return slideEntries.map((entry, index) => {
      const isLastPage = activePageIndex === totalPages - 1;
      const startIndex =
        isLastPage && slideEntries.length % slidesPerPage !== 0
          ? Math.max(slideEntries.length - slidesPerPage, 0)
          : activePageIndex * slidesPerPage;
      const endIndex = Math.min(startIndex + slidesPerPage, slideEntries.length);
      const isOnActivePage = index >= startIndex && index < endIndex;
      const isSlideActive = isCarouselActive && isOnActivePage;

      const slidePage = Math.floor(index / slidesPerPage);
      const isInLazyMountWindow =
        !lazyMountEnabled || Math.abs(slidePage - activePageIndex) <= LAZY_MOUNT_PAGE_RADIUS;

      if (!isInLazyMountWindow) {
        return (
          <SlidePlaceholder index={index} key={entry.key} snapPointIndexes={snapPointIndexes} />
        );
      }

      if (entry.kind === 'favorite') {
        return (
          <FavoriteSlide
            favorite={entry.favorite}
            index={index}
            isActive={isSlideActive}
            key={entry.key}
            snapPointIndexes={snapPointIndexes}
            units={units}
          />
        );
      }

      return (
        <RecommendationSlide
          index={index}
          isActive={isSlideActive}
          isOnActivePage={isOnActivePage}
          isFirstRecommendation={index === firstRecIndex}
          key={entry.key}
          onFavorited={onRecommendationFavorited}
          onImpression={onRecommendationImpression}
          onVisibleOnActivePage={onRecommendationVisibleOnActivePage}
          onHiddenFromActivePage={onRecommendationHiddenFromActivePage}
          recommendation={entry.recommendation}
          snapPointIndexes={snapPointIndexes}
          units={units}
        />
      );
    });
  }, [
    slideEntries,
    activePageIndex,
    pages.length,
    slidesPerPage,
    isCarouselActive,
    snapPointIndexes,
    units,
    lazyMountEnabled,
    firstRecIndex,
    onRecommendationFavorited,
    onRecommendationImpression,
    onRecommendationVisibleOnActivePage,
    onRecommendationHiddenFromActivePage,
  ]);

  const prevSlidesLength = useRef(slides.length);
  const prevFavCount = useRef(delayedFavorites.length);
  useEffect(() => {
    if (
      slides.length !== prevSlidesLength.current ||
      delayedFavorites.length !== prevFavCount.current
    ) {
      refresh();
    }
    prevSlidesLength.current = slides.length;
    prevFavCount.current = delayedFavorites.length;
  }, [slides.length, delayedFavorites.length, refresh]);

  useEffect(() => {
    if (!onReachingEnd) return;
    if (pages.length <= 1) return;
    if (activePageIndex >= pages.length - FETCH_NEXT_PAGE_BUFFER) {
      onReachingEnd();
    }
  }, [activePageIndex, pages.length, onReachingEnd]);

  const showLoading = isLoadingRecommendations && recommendations.length === 0;

  if (!slideEntries.length && !showLoading) return null;

  return (
    <Box
      className={classNames({
        'sl-section-container': true,
        [styles.containerDark]: mode === 'dark',
      })}
      component="section"
      data-testid="favorites-carousel-snap"
      suppressHydrationWarning
    >
      <Box className={styles.header}>
        {title && (
          <Typography
            variant="title1"
            component="h2"
            className={styles.title}
            data-testid="favorites-carousel-snap-title"
          >
            {title}
          </Typography>
        )}
        <div className={styles.actions}>
          {callToActionButton?.link && callToActionButton?.text && (
            <WavetrakLink
              className={styles.callToActionButton}
              href={callToActionButton.link}
              onClick={onClickCallToActionButton}
              data-testid="favorites-carousel-snap-call-to-action-button"
            >
              {callToActionButton.text}
            </WavetrakLink>
          )}
          <IconButton
            className={styles.arrow}
            data-testid="favorites-carousel-snap-previous"
            disabled={!hasPrevPage}
            disableRipple
            disableFocusRipple
            disableTouchRipple
            onClick={onClickPrevious}
            size="small"
            aria-label="Favorites carousel scroll left"
          >
            <ChevronLeft />
          </IconButton>
          <IconButton
            className={styles.arrow}
            data-testid="favorites-carousel-snap-next"
            disabled={!hasNextPage}
            disableRipple
            disableFocusRipple
            disableTouchRipple
            onClick={onClickNext}
            size="small"
            aria-label="Favorites carousel scroll right"
          >
            <ChevronRight />
          </IconButton>
        </div>
      </Box>
      {showSectionTitles && (hasFavorites || hasRecs || showLoading) && (
        <div className={styles.sectionTitleBar} ref={titleBarRef}>
          {hasFavorites && (
            <span
              className={styles.sectionTitle}
              ref={favTitleRef}
              data-testid="favorites-carousel-snap-title-favorites"
            >
              Favorites
            </span>
          )}
          {(hasRecs || showLoading) && (
            <span
              className={classNames(styles.sectionTitle, styles.sectionTitleRec)}
              ref={recTitleRef}
              data-testid="favorites-carousel-snap-title-recommendations"
            >
              Recommended spots for you
            </span>
          )}
        </div>
      )}
      <div className="sl-section-full-bleed">
        <ul className={styles.scrollContainer} ref={combinedScrollRef} suppressHydrationWarning>
          {slides}
          {showLoading &&
            Array.from({ length: slidesPerPage }, (_, i) => (
              <li className={styles.item} key={`skeleton-${i}`}>
                <div className={styles.skeletonCard} />
              </li>
            ))}
        </ul>
      </div>
    </Box>
  );
};

export default FavoritesCarouselSnap;
